import React, { useState, useEffect } from 'react';

const ConsentBanner = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [preferences, setPreferences] = useState({
        ad_storage: false,
        ad_user_data: false,
        ad_personalization: false,
        analytics_storage: false,
        personalization_storage: false,
        functionality_storage: true, // Notwendig
        security_storage: true // Notwendig
    });

    const updateConsent = (newPreferences) => {
        if (window.gtag) {
            window.gtag('consent', 'update', {
                'ad_storage': newPreferences.ad_storage ? 'granted' : 'denied',
                'ad_user_data': newPreferences.ad_user_data ? 'granted' : 'denied',
                'ad_personalization': newPreferences.ad_personalization ? 'granted' : 'denied',
                'analytics_storage': newPreferences.analytics_storage ? 'granted' : 'denied',
                'personalization_storage': newPreferences.personalization_storage ? 'granted' : 'denied',
                'functionality_storage': 'granted',
                'security_storage': 'granted'
            });
        }
    };

    useEffect(() => {
        const consent = localStorage.getItem('cookieConsent');
        const storedPreferences = localStorage.getItem('cookiePreferences');

        if (!consent) {
            setIsVisible(true);
        } else if (storedPreferences) {
            try {
                const parsedPreferences = JSON.parse(storedPreferences);
                setPreferences(parsedPreferences);
                updateConsent(parsedPreferences);
            } catch (e) {
                console.error("Fehler beim Laden der Cookie-Einstellungen", e);
            }
        }

        const handleOpenSettings = () => {
            setIsVisible(true);
            setShowSettings(true);
        };

        // Event listener and global function for reliability
        window.addEventListener('openCookieSettings', handleOpenSettings);
        window.PF_OpenCookieSettings = handleOpenSettings;

        return () => {
            window.removeEventListener('openCookieSettings', handleOpenSettings);
            delete window.PF_OpenCookieSettings;
        };
    }, []);

    const handleAcceptAll = () => {
        const allGranted = {
            ad_storage: true,
            ad_user_data: true,
            ad_personalization: true,
            analytics_storage: true,
            personalization_storage: true,
            functionality_storage: true,
            security_storage: true
        };

        updateConsent(allGranted);
        localStorage.setItem('cookieConsent', 'granted');
        localStorage.setItem('cookiePreferences', JSON.stringify(allGranted));
        window.dispatchEvent(new Event('cookieConsentUpdated'));
        setIsVisible(false);
    };

    const handleRejectAll = () => {
        const allDenied = {
            ad_storage: false,
            ad_user_data: false,
            ad_personalization: false,
            analytics_storage: false,
            personalization_storage: false,
            functionality_storage: true,  // Always required
            security_storage: true        // Always required
        };

        updateConsent(allDenied);
        localStorage.setItem('cookieConsent', 'denied');
        localStorage.setItem('cookiePreferences', JSON.stringify(allDenied));
        window.dispatchEvent(new Event('cookieConsentUpdated'));
        setIsVisible(false);
    };

    const handleSaveSettings = () => {
        updateConsent(preferences);
        localStorage.setItem('cookieConsent', 'custom');
        localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
        window.dispatchEvent(new Event('cookieConsentUpdated'));
        setIsVisible(false);
    };

    const togglePreference = (key) => {
        setPreferences(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 p-4 z-[1000] flex justify-center pointer-events-none">
            <div className="pointer-events-auto w-full max-w-2xl bg-surface-containerHigh rounded-3xl shadow-elevation-3 p-6 border border-outlineVariant animate-fade-up">

                {/* Header */}
                <div className="mb-4">
                    <h3 className="text-headline-small text-surface-on mb-2">
                        {showSettings ? 'Cookie-Einstellungen' : 'Wir respektieren deine Privatsphäre'}
                    </h3>
                    {!showSettings && (
                        <p className="text-body-medium text-surface-onVariant">
                            Wir nutzen Cookies und ähnliche Technologien, um dein Erlebnis auf unserer Website zu verbessern.
                            Einige sind technisch notwendig, andere helfen uns, unser Angebot zu optimieren.
                            Mehr dazu in unserer{' '}
                            <a href="/impressum#datenschutz" className="text-primary underline hover:text-primary/80 transition-colors">
                                Datenschutzerklärung
                            </a>.
                        </p>
                    )}
                </div>

                {!showSettings ? (
                    <div className="flex flex-col sm:flex-row justify-end gap-2">
                        <button
                            onClick={() => setShowSettings(true)}
                            className="md3-btn-text rounded-full"
                        >
                            Einstellungen
                        </button>
                        <button
                            onClick={handleRejectAll}
                            className="md3-btn-tonal rounded-full"
                        >
                            Alle ablehnen
                        </button>
                        <button
                            onClick={handleAcceptAll}
                            className="md3-btn-filled rounded-full"
                        >
                            Alle akzeptieren
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                            {/* Functional (Required) */}
                            <div className="flex items-center justify-between p-2 rounded-xl hover:bg-surface-on/5">
                                <div className="flex-1 pr-4">
                                    <div className="text-title-medium text-surface-on">Notwendig</div>
                                    <div className="text-body-small text-surface-onVariant">Erforderlich für die Grundfunktionen der Seite.</div>
                                </div>
                                <div className="md3-switch">
                                    <div className="md3-switch-track cursor-not-allowed opacity-50 bg-primary border-primary" data-checked="true"></div>
                                    <div className="md3-switch-thumb left-[22px] bg-primary-on" data-checked="true"></div>
                                </div>
                            </div>

                            {/* Analytics */}
                            <div className="flex items-center justify-between p-2 rounded-xl hover:bg-surface-on/5">
                                <div className="flex-1 pr-4">
                                    <div className="text-title-medium text-surface-on">Analytics</div>
                                    <div className="text-body-small text-surface-onVariant">Hilft uns zu verstehen, wie Besucher mit der Seite interagieren.</div>
                                </div>
                                <div className="md3-switch" onClick={() => togglePreference('analytics_storage')}>
                                    <div
                                        className={`md3-switch-track ${preferences.analytics_storage ? 'bg-primary border-primary' : ''}`}
                                        data-checked={preferences.analytics_storage}
                                    ></div>
                                    <div
                                        className={`md3-switch-thumb ${preferences.analytics_storage ? 'bg-primary-on left-[22px]' : ''}`}
                                        data-checked={preferences.analytics_storage}
                                    ></div>
                                </div>
                            </div>

                            {/* Marketing */}
                            <div className="flex items-center justify-between p-2 rounded-xl hover:bg-surface-on/5">
                                <div className="flex-1 pr-4">
                                    <div className="text-title-medium text-surface-on">Marketing</div>
                                    <div className="text-body-small text-surface-onVariant">Wird verwendet, um Werbung relevanter für dich zu machen.</div>
                                </div>
                                <div className="md3-switch" onClick={() => {
                                    togglePreference('ad_storage');
                                    togglePreference('ad_user_data');
                                    togglePreference('ad_personalization');
                                }}>
                                    <div
                                        className={`md3-switch-track ${preferences.ad_storage ? 'bg-primary border-primary' : ''}`}
                                        data-checked={preferences.ad_storage}
                                    ></div>
                                    <div
                                        className={`md3-switch-thumb ${preferences.ad_storage ? 'bg-primary-on left-[22px]' : ''}`}
                                        data-checked={preferences.ad_storage}
                                    ></div>
                                </div>
                            </div>
                        </div>

                        {/* Privacy links */}
                        <div className="text-center pt-2">
                            <a href="/impressum#datenschutz" className="text-body-small text-primary underline hover:text-primary/80 transition-colors mr-4">
                                Datenschutzerklärung
                            </a>
                            <a href="/impressum" className="text-body-small text-surface-onVariant underline hover:text-surface-on transition-colors">
                                Impressum
                            </a>
                        </div>

                        <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4 border-t border-outlineVariant">
                            <button
                                onClick={() => setShowSettings(false)}
                                className="md3-btn-text rounded-full"
                            >
                                Zurück
                            </button>
                            <button
                                onClick={handleRejectAll}
                                className="md3-btn-text rounded-full"
                            >
                                Alle ablehnen
                            </button>
                            <button
                                onClick={handleSaveSettings}
                                className="md3-btn-tonal rounded-full bg-tertiary-container text-tertiary-onContainer hover:bg-tertiary-container/80"
                            >
                                Speichern
                            </button>
                            <button
                                onClick={handleAcceptAll}
                                className="md3-btn-filled rounded-full"
                            >
                                Alle akzeptieren
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConsentBanner;
